import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Stage, Layer, Rect, Group, Line, Circle } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import {
    setZoom,
    setPan,
    selectScreen,
    selectElement,
    updateScreenPosition,
    initializeScreenPositions,
    endDragFromPalette,
    setDragHover,
} from './utils/state/canvasSlice';
import { updateScreenUIElementMetadata } from './utils/state/screensSlice';
import { createElementWithUndo } from './utils/undoRedo/actionHelpers';
import ScreenCanvasNode from './ScreenCanvasNode';
import ElementContextMenu from './ElementContextMenu';
import { elementClipboard } from './utils/clipboard';
import {
    deleteElementWithUndo,
    copyElementWithUndo,
} from './utils/undoRedo/actionHelpers';
import ConnectionLayer from './ConnectionLayer';
import NavigationLegend from './NavigationLegend';
import TemplateLibrary from './TemplateLibrary';
import {
    getDeviceDimensions,
    DEVICE_DEFINITIONS,
    DEFAULT_DEVICE,
} from './deviceDefinitions';
import Konva from 'konva';

interface InfiniteCanvasProps {
    screens: ScreenDefinition[];
    width: number;
    height: number;
    showConnections?: boolean;
    onExitEditor?: () => void;
    onCreateScreen?: (screenData: Partial<ScreenDefinition>) => void;
    isMainExperience?: boolean;
    onAddElementToSharingStation?: (
        sharingName: string,
        screenId: string,
        elementType: UIElementType,
        gridPosition: { h: string; v: string },
        groupId?: string,
    ) => void;
}

export default function InfiniteCanvas({
    screens,
    width,
    height,
    showConnections = true,
    onExitEditor,
    onCreateScreen,
    isMainExperience = true,
    onAddElementToSharingStation,
}: InfiniteCanvasProps) {
    const dispatch = useDispatch();
    const stageRef = useRef<Konva.Stage>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [showLegend, setShowLegend] = useState(false);
    const [showTemplates, setShowTemplates] = useState(false);

    // Context menu state
    const [contextMenu, setContextMenu] = useState<{
        isOpen: boolean;
        position: { x: number; y: number };
        element: UIElement | null;
        screenId: string;
    }>({
        isOpen: false,
        position: { x: 0, y: 0 },
        element: null,
        screenId: '',
    });

    // Element drag state for offset adjustment
    const [elementDragState, setElementDragState] = useState<{
        isDragging: boolean;
        elementId: string | null;
        screenId: string | null;
        startPosition: { x: number; y: number };
        initialOffsets: { horizontal: number; vertical: number };
    }>({
        isDragging: false,
        elementId: null,
        screenId: null,
        startPosition: { x: 0, y: 0 },
        initialOffsets: { horizontal: 0, vertical: 0 },
    });

    const {
        zoom,
        panX,
        panY,
        mode,
        selectedScreenId,
        selectedElementId,
        screenPositions,
        isDragFromPalette,
        draggedElementType,
        dragHoverScreen,
        dragHoverPosition,
        experienceDevices,
        activeTab,
    } = useSelector((state: any) => state.canvas);

    // State to track pending element selection after drop
    const [pendingElementSelection, setPendingElementSelection] = useState<{
        screenId: string;
        elementType: UIElementType;
        gridPosition: { h: string; v: string };
        timestamp: number;
    } | null>(null);

    // Initialize screen positions when screens change
    useEffect(() => {
        if (screens.length > 0) {
            dispatch(initializeScreenPositions(screens));
        }
    }, [screens, dispatch]);

    // Prevent unwanted scrolling when elements are selected
    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return;

        const stageContainer = stage.container();
        if (!stageContainer) return;

        // Prevent the stage from receiving focus
        const preventFocus = (e: FocusEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.target instanceof HTMLElement) {
                e.target.blur();
            }
        };

        // Prevent scroll behavior on focus
        const preventScroll = (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
        };

        stageContainer.addEventListener('focus', preventFocus, true);
        stageContainer.addEventListener('scroll', preventScroll, true);
        stageContainer.addEventListener('scrollIntoView', preventScroll, true);

        // Get the canvas container
        const canvasContainer = document.getElementById('canvas-container');

        // Override scrollIntoView method on stage container and all child elements
        const originalScrollIntoView = stageContainer.scrollIntoView;
        stageContainer.scrollIntoView = () => {
            // Do nothing - prevent scrollIntoView
        };

        // Override scrollIntoView for all canvas elements
        const overrideScrollIntoView = (element: Element) => {
            if (
                element &&
                typeof (element as any).scrollIntoView === 'function'
            ) {
                (element as any).scrollIntoView = () => {
                    // Do nothing - prevent scrollIntoView
                };
            }
        };

        // Apply to all existing elements
        const allElements = stageContainer.querySelectorAll('*');
        allElements.forEach(overrideScrollIntoView);
        overrideScrollIntoView(stageContainer);

        // Watch for new elements and override their scrollIntoView
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        overrideScrollIntoView(node as Element);
                        const childElements = (
                            node as Element
                        ).querySelectorAll('*');
                        childElements.forEach(overrideScrollIntoView);
                    }
                });
            });
        });

        observer.observe(stageContainer, {
            childList: true,
            subtree: true,
        });

        // Set tabindex to -1 to prevent focus
        stageContainer.setAttribute('tabindex', '-1');
        stageContainer.style.outline = 'none';
        stageContainer.style.userSelect = 'none';

        if (canvasContainer) {
            canvasContainer.setAttribute('tabindex', '-1');
            canvasContainer.style.outline = 'none';
        }

        return () => {
            // Disconnect observer
            observer.disconnect();

            // Restore original scrollIntoView
            stageContainer.scrollIntoView = originalScrollIntoView;

            stageContainer.removeEventListener('focus', preventFocus, true);
            stageContainer.removeEventListener('scroll', preventScroll, true);
            stageContainer.removeEventListener(
                'scrollIntoView',
                preventScroll,
                true,
            );
        };
    }, []);

    // Handle automatic selection of newly created elements
    useEffect(() => {
        if (!pendingElementSelection) return;

        const { screenId, elementType, gridPosition, timestamp } =
            pendingElementSelection;
        const screen = screens.find((s) => s.id === screenId);

        if (screen?.elements) {
            // Find the most recently created element at this position
            const newElement = screen.elements
                .filter(
                    (el) =>
                        el.verticalLocation === gridPosition.v &&
                        el.horizontalLocation === gridPosition.h &&
                        el.type === elementType,
                )
                .sort((a, b) => {
                    // Extract timestamp from ID for sorting
                    const aTime = parseInt(a.id.split('-')[2]);
                    const bTime = parseInt(b.id.split('-')[2]);
                    return bTime - aTime; // Most recent first
                })
                .find((el) => {
                    // Only consider elements created after our timestamp
                    const elTime = parseInt(el.id.split('-')[2]);
                    return elTime >= timestamp;
                });

            if (newElement) {
                console.log(
                    'Auto-selecting newly created element:',
                    newElement.id,
                );
                dispatch(selectElement(newElement.id));
                setPendingElementSelection(null);
            }
        }
    }, [pendingElementSelection, screens, dispatch]);

    // Handle wheel zoom
    const handleWheel = useCallback(
        (e: any) => {
            e.evt.preventDefault();

            const stage = stageRef.current;
            if (!stage) return;

            const oldScale = stage.scaleX();
            const pointer = stage.getPointerPosition();
            if (!pointer) return;

            const mousePointTo = {
                x: (pointer.x - stage.x()) / oldScale,
                y: (pointer.y - stage.y()) / oldScale,
            };

            const newScale = Math.max(
                0.1,
                Math.min(3, oldScale - e.evt.deltaY * 0.001),
            );

            const newPos = {
                x: pointer.x - mousePointTo.x * newScale,
                y: pointer.y - mousePointTo.y * newScale,
            };

            dispatch(setZoom(newScale));
            dispatch(setPan(newPos));
        },
        [dispatch],
    );

    // Handle stage drag for panning
    const handleStageDragStart = useCallback(
        (e: any) => {
            // Don't start canvas dragging if we're already dragging an element
            if (elementDragState.isDragging) {
                return;
            }

            // Prevent the stage from receiving focus to avoid unwanted scrolling
            e.evt?.preventDefault();

            // Pan by default unless an element is selected (similar to flow editor)
            const shouldPan =
                mode === 'pan' || (!selectedScreenId && !selectedElementId);

            if (shouldPan) {
                setIsDragging(true);
                const pos = e.target.getStage().getPointerPosition();
                setDragStart(pos);
            }
        },
        [
            mode,
            selectedScreenId,
            selectedElementId,
            elementDragState.isDragging,
        ],
    );

    const handleStageDragMove = useCallback(
        (e: any) => {
            // Don't handle canvas dragging if we're dragging an element
            if (elementDragState.isDragging) {
                return;
            }

            // Pan by default unless an element is selected (similar to flow editor)
            const shouldPan =
                mode === 'pan' || (!selectedScreenId && !selectedElementId);

            if (isDragging && shouldPan) {
                const stage = e.target.getStage();
                const pos = stage.getPointerPosition();
                const dx = pos.x - dragStart.x;
                const dy = pos.y - dragStart.y;

                dispatch(setPan({ x: panX + dx, y: panY + dy }));
                setDragStart(pos);
            }
        },
        [
            isDragging,
            mode,
            selectedScreenId,
            selectedElementId,
            elementDragState.isDragging,
            dragStart,
            panX,
            panY,
            dispatch,
        ],
    );

    const handleStageDragEnd = useCallback(() => {
        // Don't handle canvas drag end if we're dragging an element
        if (elementDragState.isDragging) {
            return;
        }

        setIsDragging(false);
    }, [elementDragState.isDragging]);

    // Handle clicking on empty canvas
    const handleStageClick = useCallback(
        (e: any) => {
            // Ignore right-clicks to prevent deselection during context menu
            if (e.evt && e.evt.button === 2) {
                return;
            }

            // Prevent the stage from receiving focus to avoid unwanted scrolling
            e.evt?.preventDefault();

            // If clicking on the stage (not a screen), deselect
            if (e.target === e.target.getStage()) {
                dispatch(selectScreen(undefined));
            }
        },
        [dispatch],
    );

    // Handle screen position updates
    const handleScreenDragEnd = useCallback(
        (screenId: string, newPosition: { x: number; y: number }) => {
            dispatch(
                updateScreenPosition({
                    screenId,
                    x: newPosition.x,
                    y: newPosition.y,
                }),
            );
        },
        [dispatch],
    );

    // Handle screen selection
    const handleScreenSelect = useCallback(
        (screenId: string) => {
            dispatch(selectScreen(screenId));
        },
        [dispatch],
    );

    // Handle element selection
    const handleElementSelect = useCallback(
        (elementId: string) => {
            dispatch(selectElement(elementId));
        },
        [dispatch],
    );

    // Handle zoom to screen
    const handleZoomToScreen = useCallback(
        (screenId: string) => {
            const screenPosition = screenPositions[screenId];
            if (!screenPosition) return;

            // Calculate the center of the screen
            const screenCenterX = screenPosition.x + 140; // SCREEN_WIDTH / 2
            const screenCenterY = screenPosition.y + 200; // SCREEN_HEIGHT / 2

            // Calculate desired zoom level (make screen take up ~60% of viewport)
            const desiredScreenWidth = width * 0.6;
            const targetZoom = Math.min(desiredScreenWidth / 280, 3); // Max zoom of 3x

            // Calculate new pan position to center the screen
            const newPanX = width / 2 - screenCenterX * targetZoom;
            const newPanY = height / 2 - screenCenterY * targetZoom;

            // Animate to new position
            dispatch(setZoom(targetZoom));
            dispatch(setPan({ x: newPanX, y: newPanY }));
            dispatch(selectScreen(screenId));
        },
        [dispatch, screenPositions, width, height],
    );

    // Handle element drop from palette
    const handleElementDrop = useCallback(
        (
            screenId: string,
            elementType: UIElementType,
            gridPosition: { h: string; v: string },
            groupId?: string,
        ) => {
            console.log(
                'Dropping element:',
                elementType,
                'at position:',
                gridPosition,
                'on screen:',
                screenId,
                groupId ? `into group: ${groupId}` : '',
                'isMainExperience:',
                isMainExperience,
                'activeTab:',
                activeTab,
            );

            const timestamp = Date.now();

            if (isMainExperience) {
                // Create element in main experience with undo support
                createElementWithUndo(
                    dispatch,
                    screenId,
                    gridPosition.h,
                    gridPosition.v,
                    elementType,
                    0, // Default to layer 0
                );

                // Set pending selection to find the element after Redux updates
                setPendingElementSelection({
                    screenId,
                    elementType,
                    gridPosition,
                    timestamp,
                });
            } else {
                // Create element in sharing station
                if (onAddElementToSharingStation && activeTab !== 'main') {
                    onAddElementToSharingStation(
                        activeTab,
                        screenId,
                        elementType,
                        gridPosition,
                        groupId,
                    );
                }
            }

            // Select the screen
            dispatch(selectScreen(screenId));

            // End the drag operation
            dispatch(endDragFromPalette());
        },
        [dispatch, isMainExperience, activeTab, onAddElementToSharingStation],
    );

    // Throttle drag over updates for better performance
    const lastDragUpdate = useRef({ screenId: '', position: { h: '', v: '' } });

    // Handle HTML5 drag and drop events
    const handleDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';

            // Only calculate hover position if we're dragging from palette
            if (!isDragFromPalette) return;

            // Get the canvas coordinates relative to the stage
            const stage = stageRef.current;
            if (!stage) return;

            const stageContainer = stage.container();
            const rect = stageContainer.getBoundingClientRect();

            // Get the actual stage transformation values instead of relying on Redux pan values
            const actualPanX = stage.x();
            const actualPanY = stage.y();
            const actualZoom = stage.scaleX();

            // Calculate coordinates using the actual stage transformation
            const x = (e.clientX - rect.left - actualPanX) / actualZoom;
            const y = (e.clientY - rect.top - actualPanY) / actualZoom;

            // Find which screen this position corresponds to
            let targetScreen = null;
            let relativePosition = { x: 0, y: 0 };

            // Calculate device-aware screen dimensions for hit testing
            const CANVAS_SCALE = 0.25;
            const currentDevice =
                experienceDevices[activeTab] || experienceDevices.main;
            const device =
                DEVICE_DEFINITIONS.find(
                    (d) => d.id === currentDevice.deviceId,
                ) || DEFAULT_DEVICE;
            const deviceDimensions = getDeviceDimensions(
                device,
                currentDevice.orientation,
            );
            const SCREEN_WIDTH = deviceDimensions.width * CANVAS_SCALE;
            const SCREEN_HEIGHT = deviceDimensions.height * CANVAS_SCALE;

            for (const screen of screens) {
                const screenPos = screenPositions[screen.id] || { x: 0, y: 0 };

                if (
                    x >= screenPos.x &&
                    x <= screenPos.x + SCREEN_WIDTH &&
                    y >= screenPos.y &&
                    y <= screenPos.y + SCREEN_HEIGHT
                ) {
                    targetScreen = screen;
                    relativePosition = {
                        x: x - screenPos.x,
                        y: y - screenPos.y,
                    };
                    break;
                }
            }

            if (!targetScreen) {
                // Only update if state actually changed
                if (lastDragUpdate.current.screenId !== '') {
                    lastDragUpdate.current = {
                        screenId: '',
                        position: { h: '', v: '' },
                    };
                    dispatch(
                        setDragHover({
                            screenId: undefined,
                            position: undefined,
                        }),
                    );
                }
                return;
            }

            // Calculate grid position within the screen using padding-aware coordinates
            // (CANVAS_SCALE, deviceDimensions, SCREEN_WIDTH, SCREEN_HEIGHT already calculated above)

            // Apply padding settings to match ScreenCanvasNode rendering
            const horizontalPadding =
                (targetScreen.displayProperties.horizontalPadding || 0) *
                CANVAS_SCALE;
            const verticalPadding =
                (targetScreen.displayProperties.verticalPadding || 0) *
                CANVAS_SCALE;

            const previewPadding = 8;
            const contentX = previewPadding + horizontalPadding;
            const contentY = previewPadding + verticalPadding;
            const contentWidth =
                SCREEN_WIDTH - previewPadding * 2 - horizontalPadding * 2;
            const contentHeight =
                SCREEN_HEIGHT - previewPadding * 2 - verticalPadding * 2;

            // Check if we're within the content area
            if (
                relativePosition.x < contentX ||
                relativePosition.x >= contentX + contentWidth ||
                relativePosition.y < contentY ||
                relativePosition.y >= contentY + contentHeight
            ) {
                // Outside content area, clear hover
                if (lastDragUpdate.current.screenId !== '') {
                    lastDragUpdate.current = {
                        screenId: '',
                        position: { h: '', v: '' },
                    };
                    dispatch(
                        setDragHover({
                            screenId: undefined,
                            position: undefined,
                        }),
                    );
                }
                return;
            }

            // Calculate grid position (3x3 grid) within the content area
            const previewX = relativePosition.x - contentX;
            const previewY = relativePosition.y - contentY;
            const gridH =
                previewX < 0 || previewX >= contentWidth
                    ? null // Outside content area
                    : previewX < contentWidth / 3
                    ? 'leading'
                    : previewX < (contentWidth / 3) * 2
                    ? 'center'
                    : 'trailing';
            const gridV =
                previewY < 0 || previewY >= contentHeight
                    ? null // Outside content area
                    : previewY < contentHeight / 3
                    ? 'top'
                    : previewY < (contentHeight / 3) * 2
                    ? 'center'
                    : 'bottom';

            // If outside the preview area, clear hover
            if (!gridH || !gridV) {
                if (lastDragUpdate.current.screenId !== '') {
                    lastDragUpdate.current = {
                        screenId: '',
                        position: { h: '', v: '' },
                    };
                    dispatch(
                        setDragHover({
                            screenId: undefined,
                            position: undefined,
                        }),
                    );
                }
                return;
            }

            // Only update Redux state if the position actually changed
            const newScreenId = targetScreen.id;
            const newPosition = { h: gridH, v: gridV };

            if (
                lastDragUpdate.current.screenId !== newScreenId ||
                lastDragUpdate.current.position.h !== newPosition.h ||
                lastDragUpdate.current.position.v !== newPosition.v
            ) {
                lastDragUpdate.current = {
                    screenId: newScreenId,
                    position: newPosition,
                };
                dispatch(
                    setDragHover({
                        screenId: newScreenId,
                        position: newPosition,
                    }),
                );
            }
        },
        [
            isDragFromPalette,
            screens,
            screenPositions,
            experienceDevices,
            activeTab,
            dispatch,
        ],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            const elementType = e.dataTransfer.getData(
                'application/element-type',
            ) as UIElementType;

            if (!elementType) return;

            // Get the canvas coordinates relative to the stage
            const stage = stageRef.current;
            if (!stage) return;

            const stageContainer = stage.container();
            const rect = stageContainer.getBoundingClientRect();

            // Get the actual stage transformation values instead of relying on Redux pan values
            const actualPanX = stage.x();
            const actualPanY = stage.y();
            const actualZoom = stage.scaleX();

            // Calculate coordinates using the actual stage transformation
            const x = (e.clientX - rect.left - actualPanX) / actualZoom;
            const y = (e.clientY - rect.top - actualPanY) / actualZoom;

            // Find which screen this position corresponds to
            let targetScreen = null;
            let relativePosition = { x: 0, y: 0 };

            // Calculate device-aware screen dimensions for hit testing
            const CANVAS_SCALE = 0.25;
            const currentDevice =
                experienceDevices[activeTab] || experienceDevices.main;
            const device =
                DEVICE_DEFINITIONS.find(
                    (d) => d.id === currentDevice.deviceId,
                ) || DEFAULT_DEVICE;
            const deviceDimensions = getDeviceDimensions(
                device,
                currentDevice.orientation,
            );
            const SCREEN_WIDTH = deviceDimensions.width * CANVAS_SCALE;
            const SCREEN_HEIGHT = deviceDimensions.height * CANVAS_SCALE;

            for (const screen of screens) {
                const screenPos = screenPositions[screen.id] || { x: 0, y: 0 };

                if (
                    x >= screenPos.x &&
                    x <= screenPos.x + SCREEN_WIDTH &&
                    y >= screenPos.y &&
                    y <= screenPos.y + SCREEN_HEIGHT
                ) {
                    targetScreen = screen;
                    relativePosition = {
                        x: x - screenPos.x,
                        y: y - screenPos.y,
                    };
                    break;
                }
            }

            if (!targetScreen) {
                console.log('Drop outside of any screen');
                dispatch(endDragFromPalette());
                return;
            }

            // Calculate grid position within the screen using padding-aware coordinates
            // (CANVAS_SCALE, deviceDimensions, SCREEN_WIDTH, SCREEN_HEIGHT already calculated above)

            // Apply padding settings to match ScreenCanvasNode rendering
            const horizontalPadding =
                (targetScreen.displayProperties.horizontalPadding || 0) *
                CANVAS_SCALE;
            const verticalPadding =
                (targetScreen.displayProperties.verticalPadding || 0) *
                CANVAS_SCALE;

            const previewPadding = 8;
            const contentX = previewPadding + horizontalPadding;
            const contentY = previewPadding + verticalPadding;
            const contentWidth =
                SCREEN_WIDTH - previewPadding * 2 - horizontalPadding * 2;
            const contentHeight =
                SCREEN_HEIGHT - previewPadding * 2 - verticalPadding * 2;

            // Check if we're within the content area
            if (
                relativePosition.x < contentX ||
                relativePosition.x >= contentX + contentWidth ||
                relativePosition.y < contentY ||
                relativePosition.y >= contentY + contentHeight
            ) {
                console.log('Drop outside content area, ignoring');
                dispatch(endDragFromPalette());
                return;
            }

            // Calculate grid position (3x3 grid) within the content area
            const previewX = relativePosition.x - contentX;
            const previewY = relativePosition.y - contentY;
            const gridH =
                previewX < 0 || previewX >= contentWidth
                    ? null // Outside content area
                    : previewX < contentWidth / 3
                    ? 'leading'
                    : previewX < (contentWidth / 3) * 2
                    ? 'center'
                    : 'trailing';
            const gridV =
                previewY < 0 || previewY >= contentHeight
                    ? null // Outside content area
                    : previewY < contentHeight / 3
                    ? 'top'
                    : previewY < (contentHeight / 3) * 2
                    ? 'center'
                    : 'bottom';

            // If outside the content area, ignore drop
            if (!gridH || !gridV) {
                console.log('Drop outside valid grid area, ignoring');
                dispatch(endDragFromPalette());
                return;
            }

            console.log(
                'Dropping on screen:',
                targetScreen.id,
                'at grid position:',
                { h: gridH, v: gridV },
            );

            handleElementDrop(targetScreen.id, elementType, {
                h: gridH,
                v: gridV,
            });
        },
        [
            screens,
            screenPositions,
            experienceDevices,
            activeTab,
            dispatch,
            handleElementDrop,
        ],
    );

    // Grid pattern for background
    const renderGrid = () => {
        const gridSize = 50;
        const gridLines = [];

        const startX = Math.floor(-panX / zoom / gridSize) * gridSize;
        const endX = Math.ceil((width - panX) / zoom / gridSize) * gridSize;
        const startY = Math.floor(-panY / zoom / gridSize) * gridSize;
        const endY = Math.ceil((height - panY) / zoom / gridSize) * gridSize;

        // Vertical lines
        for (let x = startX; x <= endX; x += gridSize) {
            gridLines.push(
                <Rect
                    key={`v-${x}`}
                    x={x}
                    y={startY}
                    width={1}
                    height={endY - startY}
                    fill="#f0f0f0"
                    opacity={0.5}
                />,
            );
        }

        // Horizontal lines
        for (let y = startY; y <= endY; y += gridSize) {
            gridLines.push(
                <Rect
                    key={`h-${y}`}
                    x={startX}
                    y={y}
                    width={endX - startX}
                    height={1}
                    fill="#f0f0f0"
                    opacity={0.5}
                />,
            );
        }

        return gridLines;
    };

    // Context menu handlers
    const handleContextMenu = useCallback(
        (element: UIElement, screenId: string, event: any) => {
            const domEvent = event.evt;
            if (domEvent) {
                setContextMenu({
                    isOpen: true,
                    position: {
                        // Use pageX/pageY which account for scrolling
                        x: domEvent.pageX + 2,
                        y: domEvent.pageY + 2,
                    },
                    element,
                    screenId,
                });
            }
        },
        [],
    );

    const handleContextMenuClose = useCallback(() => {
        setContextMenu((prev) => ({ ...prev, isOpen: false }));
    }, []);

    // Element drag handlers for offset adjustment
    const handleElementDragStart = useCallback(
        (element: UIElement, screenId: string, event: any) => {
            const domEvent = event.evt;
            if (domEvent) {
                // Get initial offset values
                const metadata = element.metadata as any;
                const initialHorizontalOffset = metadata.horizontalOffset || 0;
                const initialVerticalOffset = metadata.verticalOffset || 0;

                setElementDragState({
                    isDragging: true,
                    elementId: element.id,
                    screenId: screenId,
                    startPosition: {
                        x: domEvent.clientX,
                        y: domEvent.clientY,
                    },
                    initialOffsets: {
                        horizontal: initialHorizontalOffset,
                        vertical: initialVerticalOffset,
                    },
                });

                // Prevent default to avoid conflicts with other drag operations
                domEvent.preventDefault();
            }
        },
        [],
    );

    const handleElementDragMove = useCallback(
        (event: MouseEvent) => {
            if (
                !elementDragState.isDragging ||
                !elementDragState.elementId ||
                !elementDragState.screenId
            ) {
                return;
            }

            // Calculate mouse movement delta
            const deltaX = event.clientX - elementDragState.startPosition.x;
            const deltaY = event.clientY - elementDragState.startPosition.y;

            // Convert pixel movement to offset values
            // Scale factor to make dragging feel natural (adjust as needed)
            const scaleFactor = 2;
            const newHorizontalOffset =
                elementDragState.initialOffsets.horizontal +
                deltaX * scaleFactor;
            const newVerticalOffset =
                elementDragState.initialOffsets.vertical + deltaY * scaleFactor;

            // Update the element's offsets
            dispatch(
                updateScreenUIElementMetadata({
                    screenId: elementDragState.screenId,
                    elementId: elementDragState.elementId,
                    key: 'horizontalOffset',
                    value: Math.round(newHorizontalOffset),
                }),
            );

            dispatch(
                updateScreenUIElementMetadata({
                    screenId: elementDragState.screenId,
                    elementId: elementDragState.elementId,
                    key: 'verticalOffset',
                    value: Math.round(newVerticalOffset),
                }),
            );
        },
        [elementDragState, dispatch],
    );

    const handleElementDragEnd = useCallback(() => {
        if (elementDragState.isDragging) {
            setElementDragState({
                isDragging: false,
                elementId: null,
                screenId: null,
                startPosition: { x: 0, y: 0 },
                initialOffsets: { horizontal: 0, vertical: 0 },
            });
        }
    }, [elementDragState.isDragging]);

    // Add global mouse event listeners for element dragging
    useEffect(() => {
        if (elementDragState.isDragging) {
            document.addEventListener('mousemove', handleElementDragMove);
            document.addEventListener('mouseup', handleElementDragEnd);

            return () => {
                document.removeEventListener(
                    'mousemove',
                    handleElementDragMove,
                );
                document.removeEventListener('mouseup', handleElementDragEnd);
            };
        }
    }, [
        elementDragState.isDragging,
        handleElementDragMove,
        handleElementDragEnd,
    ]);

    const handleDeleteElement = useCallback(() => {
        if (contextMenu.element && contextMenu.screenId) {
            deleteElementWithUndo(
                dispatch,
                contextMenu.element.id,
                contextMenu.screenId,
                contextMenu.element,
            );
        }
    }, [dispatch, contextMenu]);

    const handleCopyElement = useCallback(() => {
        if (contextMenu.element) {
            elementClipboard.copy(contextMenu.element);
        }
    }, [contextMenu]);

    const handlePasteElement = useCallback(() => {
        const pastedElement = elementClipboard.paste();
        if (pastedElement && contextMenu.element && contextMenu.screenId) {
            // Paste in the same location as the context menu element
            copyElementWithUndo(
                dispatch,
                pastedElement,
                contextMenu.screenId,
                contextMenu.element.horizontalLocation,
                contextMenu.element.verticalLocation,
            );
        }
    }, [dispatch, contextMenu]);

    return (
        <div className="relative w-full h-full bg-muted">
            <NavigationLegend
                isOpen={showLegend}
                onClose={() => setShowLegend(false)}
            />
            <TemplateLibrary
                isOpen={showTemplates}
                onClose={() => setShowTemplates(false)}
                onCreateScreen={onCreateScreen}
            />
            <Stage
                ref={stageRef}
                width={width}
                height={height}
                scaleX={zoom}
                scaleY={zoom}
                x={panX}
                y={panY}
                onWheel={handleWheel}
                onMouseDown={handleStageDragStart}
                onMouseMove={handleStageDragMove}
                onMouseUp={handleStageDragEnd}
                onClick={handleStageClick}
                draggable={
                    mode === 'pan' || (!selectedScreenId && !selectedElementId)
                }
                listening={true}
                preventDefault={false}
            >
                {/* Background grid */}
                <Layer>{renderGrid()}</Layer>

                {/* Connection lines */}
                <ConnectionLayer
                    screens={screens}
                    screenPositions={screenPositions}
                    showConnections={showConnections}
                />

                {/* Screen nodes */}
                <Layer>
                    {screens.map((screen) => (
                        <ScreenCanvasNode
                            key={`${screen.id}-${JSON.stringify(
                                screen.displayProperties,
                            )}`}
                            screen={screen}
                            position={
                                screenPositions[screen.id] || { x: 0, y: 0 }
                            }
                            isSelected={selectedScreenId === screen.id}
                            selectedElementId={selectedElementId}
                            onSelect={() => handleScreenSelect(screen.id)}
                            onZoomToScreen={() => handleZoomToScreen(screen.id)}
                            onElementSelect={handleElementSelect}
                            onDragEnd={(newPos) =>
                                handleScreenDragEnd(screen.id, newPos)
                            }
                            isDraggable={mode === 'select'}
                            onElementDrop={(
                                elementType,
                                gridPosition,
                                groupId,
                            ) =>
                                handleElementDrop(
                                    screen.id,
                                    elementType,
                                    gridPosition,
                                    groupId,
                                )
                            }
                            isDragFromPalette={isDragFromPalette}
                            draggedElementType={draggedElementType}
                            dragHoverPosition={
                                dragHoverScreen === screen.id
                                    ? dragHoverPosition
                                    : undefined
                            }
                            onRightClick={handleContextMenu}
                            onElementDragStart={handleElementDragStart}
                        />
                    ))}
                </Layer>

                {/* Start and Exit indicators */}
                {screens.length > 0 && (
                    <Layer>
                        {(() => {
                            const indicators = [];

                            // Get device configuration for consistent dimensions
                            const CANVAS_SCALE = 0.25;
                            const currentDevice = experienceDevices[
                                activeTab
                            ] || {
                                deviceId: DEFAULT_DEVICE.id,
                                orientation: 'portrait',
                            };
                            const device =
                                DEVICE_DEFINITIONS.find(
                                    (d) => d.id === currentDevice.deviceId,
                                ) || DEFAULT_DEVICE;
                            const deviceDimensions = getDeviceDimensions(
                                device,
                                currentDevice.orientation,
                            );
                            const SCREEN_WIDTH =
                                deviceDimensions.width * CANVAS_SCALE;
                            const SCREEN_HEIGHT =
                                deviceDimensions.height * CANVAS_SCALE;

                            // Start indicator for first screen
                            const firstScreen = screens[0];
                            const firstScreenPosition = screenPositions[
                                firstScreen.id
                            ] || { x: 50, y: 50 };

                            // Position start indicator at the vertical middle of the screen's left edge
                            const startCircleX = firstScreenPosition.x - 30;
                            const startCircleY =
                                firstScreenPosition.y + SCREEN_HEIGHT / 2; // Exact vertical middle

                            indicators.push(
                                <Group key="start-indicator">
                                    {/* Green circle outline for start */}
                                    <Circle
                                        x={startCircleX}
                                        y={startCircleY}
                                        radius={12}
                                        stroke="#10B981"
                                        strokeWidth={3}
                                        fill="transparent"
                                    />

                                    {/* Short horizontal line connecting to screen */}
                                    <Line
                                        points={[
                                            startCircleX + 12,
                                            startCircleY, // Start from right edge of circle
                                            firstScreenPosition.x,
                                            startCircleY, // End at left edge of screen
                                        ]}
                                        stroke="#10B981"
                                        strokeWidth={2.5}
                                        lineCap="round"
                                    />
                                </Group>,
                            );

                            // Exit indicators for screens with reset/share buttons
                            screens.forEach((screen) => {
                                const screenPos = screenPositions[
                                    screen.id
                                ] || { x: 0, y: 0 };
                                const exitButtons = screen.elements.filter(
                                    (element) => {
                                        if (element.type !== 'button')
                                            return false;
                                        const metadata =
                                            element.metadata as any;
                                        const action = metadata?.action;

                                        // Check for exit actions (reset, share, shareAndReset, etc.)
                                        return (
                                            action === 'reset' ||
                                            action === 'share' ||
                                            action === 'shareAndReset' ||
                                            action === 'shareAndPrint' ||
                                            (typeof action === 'string' &&
                                                (action.includes('reset') ||
                                                    action.includes('share')))
                                        );
                                    },
                                );

                                if (exitButtons.length > 0) {
                                    // Position exit indicator touching the right edge at vertical middle
                                    const exitCircleX =
                                        screenPos.x + SCREEN_WIDTH + 30; // Right edge + margin
                                    const exitCircleY =
                                        screenPos.y + SCREEN_HEIGHT / 2; // Exact vertical middle

                                    indicators.push(
                                        <Group
                                            key={`exit-indicator-${screen.id}`}
                                        >
                                            {/* Short horizontal line connecting from screen */}
                                            <Line
                                                points={[
                                                    screenPos.x + SCREEN_WIDTH,
                                                    exitCircleY, // Start from right edge of screen
                                                    exitCircleX - 12,
                                                    exitCircleY, // End at left edge of circle
                                                ]}
                                                stroke="#EF4444"
                                                strokeWidth={2.5}
                                                lineCap="round"
                                            />

                                            {/* Red circle outline for exit */}
                                            <Circle
                                                x={exitCircleX}
                                                y={exitCircleY}
                                                radius={12}
                                                stroke="#EF4444"
                                                strokeWidth={3}
                                                fill="transparent"
                                            />
                                        </Group>,
                                    );
                                }
                            });

                            return indicators;
                        })()}
                    </Layer>
                )}
            </Stage>

            {/* HTML overlay for drag and drop - only render when dragging */}
            {isDragFromPalette && (
                <div
                    className="absolute inset-0"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    style={{
                        pointerEvents: 'auto',
                        background: 'transparent',
                    }}
                />
            )}

            {/* Context Menu - rendered outside Konva */}
            <ElementContextMenu
                isOpen={contextMenu.isOpen}
                position={contextMenu.position}
                onClose={handleContextMenuClose}
                onDelete={handleDeleteElement}
                onCopy={handleCopyElement}
                onPaste={handlePasteElement}
                canPaste={elementClipboard.canPaste()}
            />
        </div>
    );
}
